import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { observable } from 'mobx';
import {
  FormGroup, InputGroup,
  Button,
  Intent,
  TextArea
} from "@blueprintjs/core";
import { FormSection, Choice, FieldError, MoreOptions } from 'components/FormBits';
import { PaymentSummary, formatMoney } from './TransactionModal';
import { AMOUNT_CHOICES, amountChoiceFor, amountFor } from './ManualTransactionModal';
import Toast from 'helpers/toaster';
import FormDrawer from 'components/FormDrawer';
import { currency, currencySymbol } from 'helpers/currency';
import LocationAutocomplete from "components/LocationAutocomplete";
import {StripeProvider, Elements, injectStripe, CardElement } from 'react-stripe-elements';

// The footer's "Charge card" button submits this form from outside it (the
// panel footer sits outside the Stripe Elements form), via the form attribute.
const CARD_PAYMENT_FORM_ID = 'card-payment-form';

const createOptions = (fontSize, padding) => {
  return {
    style: {
      base: {
        fontSize,
        color: '#424770',
        letterSpacing: '0.025em',
        fontFamily: 'Helvetica, sans-serif',
        '::placeholder': {
          color: '#aab7c4',
        },
        padding,
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };
};

@inject('invoiceStore', 'paymentStore') @observer
class _StripeForm extends Component {

  @observable transaction = {
    amount: this.props.due_amount,
    payment_method: "Card",
    notes: "",
    token: "",
    description: "",
    invoice_id: this.props.invoice_id,
    location_id: null
  }

  @observable location = null

  @observable errors = {}

  amountInput = null

  componentDidMount() {
    // The footer's "Charge $…" button shows the amount typed here.
    this.reportAmount();
  }

  reportAmount() {
    if (this.props.onAmountChange) this.props.onAmountChange(this.transaction.amount);
  }

  setAmount(value) {
    this.transaction.amount = value;
    if (this.errors.amount) this.errors = {};
    this.reportAmount();
  }

  onAmountChoice(choice) {
    this.setAmount(amountFor(choice, this.props.due_amount));
    if (choice === 'other' && this.amountInput) this.amountInput.focus();
  }

  // Checked before anything goes to Stripe: an amount above zero and not more
  // than the balance (the same limit as a cash payment).
  validate() {
    const errors = {};
    const raw = String(this.transaction.amount === null || this.transaction.amount === undefined ? '' : this.transaction.amount).trim();
    const amount = parseFloat(raw);
    const due = parseFloat(this.props.due_amount);
    if (!raw || isNaN(amount)) {
      errors.amount = 'Enter the amount to charge, e.g. 120.00.';
    } else if (amount <= 0) {
      errors.amount = 'The amount must be more than zero.';
    } else if (!isNaN(due) && amount > due) {
      errors.amount = `That's more than the balance owed (${formatMoney(due, this.props.currency_symbol)}).`;
    }
    this.errors = errors;
    return Object.keys(errors).length === 0;
  }

  componentWillMount() {
    let {
      props: {
        invoiceStore: {
          invoice: {
            object: {
              attributes
            }
          }
        }
      }
    } = this;

    if(attributes && attributes.location && attributes.location.id) {
      this.location = {attributes: attributes.location}
      this.transaction.location_id = attributes.location.id
    }
  }

  handleChange = (change) => {
    // console.log('[change]', change);
  };

  handleSubmit = (event) => {
    event.preventDefault();
    // Already charging: Enter in a field must not start a second charge.
    if(this.props.paymentStore.showLoaderCardPayment) return;
    if(!this.validate()) {
      if(this.amountInput) this.amountInput.focus();
      return;
    }

    if(this.props.stripe) {
      this.props.paymentStore.showLoaderCardPayment = true;
      this.props.stripe.createToken()
        .then(this.processStripePayment.bind(this));
    } else {
      console.log("Stripe.js hasn't loaded yet.");
    }
  }

  processStripePayment(payload) {
    // Card declined by Stripe.js (incomplete or invalid card): no token, so
    // stop the spinner (it keeps the panel open) and say why.
    if(!payload || !payload.token) {
      this.props.paymentStore.showLoaderCardPayment = false;
      Toast.show({ icon: 'warning-sign', message: (payload && payload.error && payload.error.message) || 'Unable to charge!', intent: Intent.DANGER })
      return;
    }
    this.transaction.token = payload.token.id
    this.props.paymentStore.stripeCharge(this.transaction)
      .then(this.onSuccessfulStripeCharge.bind(this))
      .catch(this.onFailStripeCharge.bind(this))
  }

  onSuccessfulStripeCharge(response) {
    Toast.show({ icon: 'thumbs-up', message: "Successfully charge!", intent: Intent.SUCCESS })
    this.props.onClose()
  }

  onFailStripeCharge(error) {
    if(error && error.response && error.response.data && error.response.data.errors) {
      Toast.show({ icon: 'warning-sign', message: error.response.data.errors, intent: Intent.DANGER })
    } else {
      Toast.show({ icon: 'warning-sign', message: 'Unable to charge!', intent: Intent.DANGER })
    }
    //this.props.onClose()
  }

  onLocationClear = () => {
    this.location = null
    this.transaction.location_id = ""
  }

  render() {
    let {
      transaction,
      props: {
        invoiceStore: {
          invoice: {
            object: {
              attributes
            }
          }
        }
      }
    } = this;
    const { errors } = this;
    const due = this.props.due_amount;
    const busy = Boolean(this.props.paymentStore.showLoaderCardPayment);
    const hasMore = Boolean(transaction.description || transaction.notes);
    return(
      <form id={CARD_PAYMENT_FORM_ID} onSubmit={this.handleSubmit.bind(this)} noValidate>
        <FormSection title="How much to charge" hint="Defaults to the balance owed.">
          {due !== undefined && due !== null && parseFloat(due) > 0 && (
            <Choice
              name="Quick amount"
              value={amountChoiceFor(transaction.amount, due)}
              onChange={(choice) => this.onAmountChoice(choice)}
              options={AMOUNT_CHOICES}
              disabled={busy}
            />
          )}
          <div className={`bp3-form-group${errors.amount ? ' has-error' : ''}`}>
            <label className="fb-label" htmlFor="card-payment-amount">
              Amount ({currency()}{currencySymbol()})<span className="fb-required" aria-hidden="true"> *</span>
            </label>
            <InputGroup
              id="card-payment-amount"
              inputRef={(input) => { this.amountInput = input; }}
              type="text"
              inputMode="decimal"
              autoComplete="off"
              placeholder="e.g. 120.00"
              aria-required="true"
              aria-invalid={errors.amount ? true : undefined}
              aria-describedby={errors.amount ? 'card-payment-amount-error' : undefined}
              value={transaction.amount === null || transaction.amount === undefined ? '' : transaction.amount}
              onChange={(event) => this.setAmount(event.target.value)}
            />
            <FieldError id="card-payment-amount-error">{errors.amount}</FieldError>
          </div>
        </FormSection>

        <FormSection title="Card" hint="Card number, expiry and CVC, as the customer reads them out.">
          <FormGroup label="Card details" labelInfo="*">
            <CardElement
              onChange={this.handleChange.bind(this)}
              hidePostalCode={true}
              {...createOptions(this.props.fontSize)}
            />
          </FormGroup>

          {transaction && !transaction.location_id && <FormGroup
            label={`Location for ${attributes && attributes.client && attributes.client.name}`}
            helperText="Optional. The address this payment is for."
          >
            <LocationAutocomplete
              scope={{ client_id: attributes && attributes.client && attributes.client.id }}
              inputProps={{
                fill: true,
                placeholder: "Start typing an address, e.g. 12 Main St"
              }}
              onLocationClear={this.onLocationClear.bind(this)}
              value={this.location}
              onLocationSelect={location => {
                if (location) {
                  transaction.location_id = location.id;
                  this.location = location;
                } else {
                  this.onLocationClear();
                }
              }}
            />
          </FormGroup>}
        </FormSection>

        <MoreOptions label="Description and notes" count={2} open={hasMore}>
          <FormGroup label="Description" labelFor="card-payment-description" helperText="Shown with the charge.">
            <TextArea
              id="card-payment-description"
              fill={true}
              placeholder="e.g. Furnace repair, invoice balance"
              value={transaction.description}
              onChange={(event) => {transaction.description = event.target.value}}
            />
          </FormGroup>

          <FormGroup label="Notes" labelFor="card-payment-notes">
            <TextArea
              id="card-payment-notes"
              fill={true}
              placeholder="e.g. Customer paid over the phone"
              value={transaction.notes}
              onChange={(event) => {transaction.notes = event.target.value}}
            />
          </FormGroup>
        </MoreOptions>
      </form>
    )
  }
}

const StripeForm = injectStripe(_StripeForm);

@inject('store', 'invoiceStore', 'paymentStore', 'viewStore') @observer
class CardTransactionModal extends Component {

  // What the form's Amount says, for the "Charge $…" button.
  @observable chargeAmount = null;

  handleCardTransactionDailog() {
    this.props.onClose();
  }

  render() {
    const {
      invoiceStore: {
        invoice: {
          object
        }
      },
      paymentStore: {
        showLoaderCardPayment
      }
    } = this.props;
    const busy = Boolean(showLoaderCardPayment);
    const close = this.handleCardTransactionDailog.bind(this);
    const symbol = object.attributes.currency_symbol;
    const amount = parseFloat(this.chargeAmount);
    const chargeText = this.chargeAmount !== null && String(this.chargeAmount).trim() !== '' && !isNaN(amount)
      ? `Charge ${formatMoney(amount, symbol)}`
      : 'Charge card';
    return(
      <FormDrawer
        isOpen={this.props.isOpen}
        title="Record a card payment"
        icon="credit-card"
        onClose={close}
        busy={busy}
        footer={
          <>
            <Button text="Cancel" onClick={close} disabled={busy} />
            <Button
              type="submit"
              form={CARD_PAYMENT_FORM_ID}
              intent={Intent.PRIMARY}
              icon="credit-card"
              text={chargeText}
              loading={busy}
            />
          </>
        }
      >
        <PaymentSummary attributes={object.attributes} />
        <StripeProvider apiKey={window.stripe_api_key}>
          <Elements>
            <StripeForm
              fontSize={18}
              due_amount={object.attributes.due_amount}
              invoice_id={object.attributes.id}
              currency_symbol={symbol}
              onAmountChange={(value) => { this.chargeAmount = value; }}
              onClose={close}
            />
          </Elements>
        </StripeProvider>
      </FormDrawer>
    )
  }

}

export default CardTransactionModal;